import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import {
    faSearch,
    faEye,
    faUserShield,
    faUser,
    faChevronLeft,
    faChevronRight
} from "@fortawesome/free-solid-svg-icons";

import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../../components/Ui_Element/DotLoader";
import { getlist } from "../../../contexts/apicontexts";
import config from "../../../config";

const PAGE_SIZE = 10;
const HIDDEN_ROLE_NAMES = ["customer user", "customer"];

export default function AllUser() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [isLoader, setIsLoader] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        handleSearch();
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        setIsLoader(true);
        try {
            const response = await getlist(`${config.baseurl}Users/getall`);
            console.log(response);
            if (response?.status === 200) {
                setUsers(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setIsLoader(true);
            setTimeout(() => setIsLoader(false), 500); // Small delay for smooth feel
        }
    };

    const handleSearch = () => {
        const lowercasedSearch = searchTerm.toLowerCase();
        const filtered = users.filter(user =>
            !HIDDEN_ROLE_NAMES.includes((user.roleName || "").toLowerCase()) &&
            (
                (user.fullName && user.fullName.toLowerCase().includes(lowercasedSearch)) ||
                (user.code && user.code.toLowerCase().includes(lowercasedSearch)) ||
                (user.email && user.email.toLowerCase().includes(lowercasedSearch)) ||
                (user.mobile && user.mobile.toLowerCase().includes(lowercasedSearch))
            )

        );
        setFilteredUsers(filtered);
        setPage(1); // Reset to first page on search
    };

    const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
    const paginatedData = filteredUsers.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    const getImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("blob:") || path.startsWith("data:") || path.startsWith("http")) return path;

        let cleanPath = path;
        if (cleanPath.startsWith("~")) {
            cleanPath = cleanPath.substring(1);
        }
        if (!cleanPath.startsWith("/")) {
            cleanPath = "/" + cleanPath;
        }

        let serverRoot = config.baseurl;
        try {
            const url = new URL(config.baseurl);
            serverRoot = url.origin;
        } catch (e) {
            if (serverRoot.includes("/api")) {
                serverRoot = serverRoot.split("/api")[0];
            }
        }
        if (serverRoot.endsWith("/")) serverRoot = serverRoot.slice(0, -1);

        return `${serverRoot}${cleanPath}`;
    };

    const handleViewUser = (user) => {
        console.log("Viewing user details:", user);
        alert(`User Details:\nName: ${user.fullName}\nEmail: ${user.email}\nCode: ${user.code}`);
    };

    const handlePermission = (user) => {
        navigate(`/user/menu/permissions/${user.id}`, { state: { user } });
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
            <Breadcrumb pageName="User Management" currentPage="All Users" />

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border dark:border-gray-800 mt-4 overflow-hidden">
                {/* HEADER */}
                <div className="card-header flex flex-col md:flex-row justify-between items-center px-4 py-4 sm:px-6 border-b dark:border-gray-800 transition-colors duration-300
                    bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]
                    dark:from-[#2D120D] dark:to-[#1A1A1A] ">

                    <div className="flex items-center gap-3 mb-4 md:mb-0">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                            User List
                        </h2>
                        {isLoader && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>

                    {/* SEARCH BAR */}
                    <div className="relative w-full md:w-80">
                        <input
                            type="text"
                            placeholder="Search by name, code, or email..."
                            className="w-full pl-10 pr-4 py-2 rounded-full border-none outline-none focus:ring-2 focus:ring-white/50 text-sm bg-white/10 text-white placeholder-white/60 backdrop-blur-sm transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/60"
                        />
                    </div>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px] sm:text-sm min-w-[900px]">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-800">
                            <tr>
                                <th className="px-6 py-4 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">User Info</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Code</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                                <th className="px-6 py-4 text-center font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {paginatedData.length > 0 ? (
                                paginatedData.map((user, index) => (
                                    <tr key={index} className="hover:bg-orange-50/30 dark:hover:bg-orange-900/10 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0">
                                                    {user.photoUrlWithPath ? (
                                                        // <img
                                                        //     src={getImageUrl(user.photoUrlWithPath)}
                                                        //     alt={user.fullName}
                                                        //     className="w-10 h-10 rounded-full object-cover border-2 border-orange-100 shadow-sm"
                                                        // />
                                                        <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 border-2 border-orange-50 dark:border-orange-900/50 shadow-sm">
                                                            <FontAwesomeIcon icon={faUser} />
                                                        </div>
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 border-2 border-orange-50 dark:border-orange-900/50 shadow-sm">
                                                            <FontAwesomeIcon icon={faUser} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-gray-800 dark:text-gray-200">{user.fullName}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">ID: {user.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-700">
                                                {user.code || "N/A"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300 font-medium">{user.email}</td>
                                        <td className="px-6 py-4">
                                            {user.roleName ? (
                                                <span className="px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold border border-blue-100 dark:border-blue-900/30">
                                                    {user.roleName}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 dark:text-gray-500 italic">No Role</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{user.mobile || "N/A"}</td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handlePermission(user)}
                                                    className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-500 hover:border-blue-600 dark:hover:border-blue-500 transition-all shadow-sm"
                                                    title="Menu Permission"
                                                >
                                                    <FontAwesomeIcon icon={faUserShield} />
                                                </button>
                                                <button
                                                    onClick={() => handleViewUser(user)}
                                                    className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-orange-600 dark:text-orange-400 hover:bg-orange-600 hover:text-white dark:hover:bg-orange-500 hover:border-orange-600 dark:hover:border-orange-500 transition-all shadow-sm"
                                                    title="View Details"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400 dark:text-gray-600">
                                        <div className="flex flex-col items-center gap-2">
                                            <FontAwesomeIcon icon={faUser} size="2x" className="text-gray-200 dark:text-gray-800" />
                                            <p className="text-lg">No users found matching your search.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t dark:border-gray-800 flex items-center justify-between">
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                            Showing <span className="font-bold text-gray-800 dark:text-gray-200">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-bold text-gray-800 dark:text-gray-200">{Math.min(page * PAGE_SIZE, filteredUsers.length)}</span> of <span className="font-bold text-gray-800 dark:text-gray-200">{filteredUsers.length}</span> users
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                disabled={page === 1}
                                className="p-2 rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                                <button
                                    key={num}
                                    onClick={() => setPage(num)}
                                    className={`w-9 h-9 rounded-lg text-sm font-bold transition-all shadow-sm ${page === num
                                        ? "bg-gradient-to-r from-[#C04C2A] to-[#4A1B13] text-white shadow-[#C04C2A]/20"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    {num}
                                </button>
                            )).filter(btn => {
                                // Simple logic to truncate pagination if too many pages
                                if (totalPages <= 7) return true;
                                if (Number(btn.key) <= 1 || Number(btn.key) >= totalPages) return true;
                                if (Math.abs(Number(btn.key) - page) <= 1) return true;
                                return false;
                            })}
                            <button
                                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={page === totalPages}
                                className="p-2 rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                <FontAwesomeIcon icon={faChevronRight} size="sm" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
